import { stringifyEntityRef, type Entity } from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  entityPresentationApiRef,
} from '@backstage/plugin-catalog-react';
import useAsync from 'react-use/esm/useAsync';

export interface SortedEntity {
  ref: string;
  /** The entity's presentation title - never derived from the ref. */
  label: string;
  /** `spec[specField]`, when a specField was asked for. */
  specValue?: string;
}

/**
 * Every catalog entity of one kind, sorted alphabetically by display name.
 *
 * Shared by the single- and multi-select pickers. See SortedEntityField for why
 * the stock pickers cannot sort and why the label comes from the presentation
 * API.
 *
 * `specField` additionally fetches `spec[specField]` for each entity, so a
 * caller can filter on it.
 */
export const useSortedEntities = (
  kind: string | undefined,
  specField?: string,
) => {
  const catalogApi = useApi(catalogApiRef);
  const entityPresentationApi = useApi(entityPresentationApiRef);

  return useAsync(async (): Promise<SortedEntity[]> => {
    if (!kind) {
      throw new Error(
        'A sorted entity picker requires ui:options.catalogFilter.kind',
      );
    }
    const fields = [
      'kind',
      'metadata.name',
      'metadata.namespace',
      'metadata.title',
      'spec.profile.displayName',
      ...(specField ? [`spec.${specField}`] : []),
    ];
    const items: Entity[] = [];
    for await (const batch of catalogApi.streamEntities({
      filter: { kind },
      fields,
    })) {
      items.push(...batch);
    }

    const withLabels = await Promise.all(
      items.map(async entity => ({
        ref: stringifyEntityRef(entity),
        label: (
          await entityPresentationApi.forEntity(entity).promise
        ).primaryTitle,
        specValue: specField
          ? (entity.spec?.[specField] as string | undefined)
          : undefined,
      })),
    );
    withLabels.sort((a, b) => a.label.localeCompare(b.label));
    return withLabels;
  }, [kind, specField]);
};
